import { isChunkLoadError } from '../../forge/utils/lazyRouteModule';

export type RouteErrorKind = 'chunk' | 'not-found' | 'render';

export interface RouteErrorPresentation {
  kind: RouteErrorKind;
  title: string;
  description: string;
  /** Whether reloading the page is the action most likely to fix this. */
  reloadLikelyFixes: boolean;
  /** Single-line technical detail, shown under a disclosure for bug reports. */
  detail: string;
}

function errorDetail(error: unknown): string {
  if (error instanceof Error) {
    return error.message ? `${error.name}: ${error.message}` : error.name;
  }
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Turns whatever `useRouteError()` produced into the copy the route error
 * elements render.
 *
 * `status` is the HTTP-ish status when the router threw a route error response
 * (react-router's `isRouteErrorResponse`); the caller extracts it so this stays
 * a plain function over plain values.
 *
 * The chunk case is called out separately because it is the one failure users
 * can reliably fix themselves: a release replaced the hashed chunk an open tab
 * still points at. `loadRouteChunk` already retries that once per distinct
 * failure per session, so an error reaching here means the retry was spent or
 * unavailable, and reloading is still the right advice.
 */
export function describeRouteError(error: unknown, status?: number): RouteErrorPresentation {
  const detail = errorDetail(error);

  if (isChunkLoadError(error)) {
    return {
      kind: 'chunk',
      title: "This page couldn't finish loading",
      description:
        'Part of the app failed to download. This usually means a new version was released while this tab was open. Reloading picks up the new version.',
      reloadLikelyFixes: true,
      detail,
    };
  }

  if (status === 404) {
    return {
      kind: 'not-found',
      title: 'Page not found',
      description: 'This address does not match any page. Check the link, or head back.',
      reloadLikelyFixes: false,
      detail,
    };
  }

  return {
    kind: 'render',
    title: 'Something went wrong on this page',
    description:
      'This page hit an unexpected error and stopped rendering. The rest of the app still works, so you can navigate elsewhere and come back.',
    reloadLikelyFixes: false,
    detail,
  };
}
